/**
 * Invitation token capture for /accept-invite?token=... links (enterprise batch 8).
 *
 * Same problem as the referral code (utils/referral.ts): the Auth0 round trip
 * returns to the bare origin, so a person who opens an invite link while
 * signed out comes back from the login screen on "/" with the token gone.
 * The token is stashed before any redirect and AppRoutes sends the user back
 * to /accept-invite once they are signed in.
 *
 * localStorage, not sessionStorage: a new account has to verify its email
 * first, and the verification link usually opens in a new tab. The token is
 * useless to anyone but the invited address (the server checks it), expires
 * on the server after 14 days, and is cleared here once the page has used it.
 */

const STORAGE_KEY = 'uw_pending_invite_token';
const MAX_LENGTH = 128;

/** Call once on app load, before Auth0 can redirect. */
export function captureInviteFromUrl(): void {
  try {
    if (window.location.pathname !== '/accept-invite') return;
    const token = new URLSearchParams(window.location.search).get('token');
    if (token && token.length <= MAX_LENGTH) localStorage.setItem(STORAGE_KEY, token);
  } catch {
    // Storage can throw in private browsing; the link still works while signed in.
  }
}

export function getPendingInvite(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function clearPendingInvite(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* see captureInviteFromUrl */
  }
}
